import React from 'react';
import Button from './Button';

/**
 * Which rows a page shows, and whether there is a page on either side. Page numbers are 1-based.
 *
 * A page outside the range is clamped to the nearest real one: a list that shrank under the user
 * (a filter, a delete) should land them on its last page, not on an empty one.
 */
export function pageWindow(page: number, pageSize: number, total: number) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pages);
  return {
    page: current,
    pages,
    from: total === 0 ? 0 : (current - 1) * pageSize + 1,
    to: Math.min(current * pageSize, total),
    hasPrevious: current > 1,
    hasNext: current < pages,
  };
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** What the rows are, plural: "invoices". */
  noun?: string;
}

/**
 * The one pager. There were 13, in four label formats and two active colours (docs/DESIGN.md).
 * With no rows it renders nothing: the table's own empty state says so better than "0 of 0".
 *
 * A total that is missing counts as no rows. Several callers read it out of a summary object the
 * server may not have sent yet (`summary.invoiceCount`), and `undefined <= 0` is false — so the
 * plain comparison let `undefined` through and the whole page died inside `toLocaleString`.
 */
const Pagination: React.FC<PaginationProps> = ({ page, pageSize, total, onPageChange, noun = 'results' }) => {
  if (!Number.isFinite(total) || total <= 0) return null;
  const w = pageWindow(page, pageSize, total);
  const count = (n: number) => n.toLocaleString('en-US');
  return (
    <nav
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3 text-xs text-slate-600"
    >
      <p className="tabular-nums">
        Showing {count(w.from)}–{count(w.to)} of {count(total)} {noun}
      </p>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!w.hasPrevious} onClick={() => onPageChange(w.page - 1)}>
          Previous
        </Button>
        <Button size="sm" disabled={!w.hasNext} onClick={() => onPageChange(w.page + 1)}>
          Next
        </Button>
      </div>
    </nav>
  );
};

export default Pagination;
